import { Injectable, Type, inject } from '@angular/core';

import { Observable, ReplaySubject, merge, of } from 'rxjs';
import { first, map, take } from 'rxjs/operators';

import { getClientConfigProperties } from '../client-config/client-config.decorator';
import { ClientConfigDiff } from '../client-config/client-config.model';
import { ClientConfigService } from '../client-config/client-config.service';

/**
 * @whatItDoes Base class for lazy client configs.
 *
 * @stable
 */
export class LazyClientConfigBase {
    whenReady: Observable<void>;
    isConfigReady: boolean;
    readonly name?: string;
}

class LazyClientConfig {
    private isLoaded: boolean;
    private isLazyLoading: boolean;
    private readonly loadedStream: ReplaySubject<void> = new ReplaySubject(1);
    private readonly proxy: any;
    private readonly clientConfigKey: string;

    constructor(
        private readonly clientConfigService: ClientConfigService,
        private readonly type: Type<any>,
    ) {
        this.clientConfigKey = getClientConfigProperties(type).key;

        const isConfigLazyLoaded$ = clientConfigService.updates.pipe(map((e: ClientConfigDiff) => e.has(this.clientConfigKey)));

        const isConfigPreloaded$ = of(clientConfigService.checkConfigLoaded(this.clientConfigKey));

        merge(isConfigLazyLoaded$, isConfigPreloaded$)
            .pipe(first((isConfigLoaded) => !!isConfigLoaded))
            .subscribe(() => {
                this.isLoaded = true;
                this.loadedStream.next();
            });

        this.proxy = new Proxy(
            {},
            {
                get: (_, name: string) => {
                    switch (name) {
                        // Angular DI checks. Don't throw even when not ready yet.
                        case 'constructor':
                        case 'ngOnDestroy':
                        case 'name':
                            return undefined;
                        case 'whenReady':
                            return this.whenReady();
                        case 'isConfigReady':
                            return this.isLoaded;
                        default:
                            return this.getProperty(name);
                    }
                },
            },
        );
    }

    get config(): any {
        return this.proxy;
    }

    whenReady(): Observable<void> {
        if (!this.isLazyLoading) {
            this.isLazyLoading = true;

            this.clientConfigService
                .load('', [this.type])
                .then(() => {
                    this.loadedStream.next();
                    this.loadedStream.complete();
                })
                .catch((error: any) => {
                    this.loadedStream.error(error);
                })
                .finally(() => {
                    this.isLazyLoading = false;
                });
        }

        return this.loadedStream.pipe(take(1));
    }

    private getProperty(name: string) {
        if (this.isLoaded) {
            return this.clientConfigService.get(this.type)[name];
        }

        throw new Error(
            `Lazy client config ${this.clientConfigKey} is not loaded yet, so it's property ${name} cannot be accessed. Subscribe to whenReady() observable to wait until it's loaded.`,
        );
    }
}

/**
 * @whatItDoes Provides access to lazy client configs and their metadata.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class LazyClientConfigService {
    private readonly configs: Map<Type<any>, LazyClientConfig> = new Map();

    private readonly clientConfigService = inject(ClientConfigService);

    get<T>(configType: Type<T>): T {
        return this.getConfig(configType)?.config;
    }

    private getConfig(configType: Type<any>): LazyClientConfig | undefined {
        if (!this.configs.has(configType)) {
            const config = new LazyClientConfig(this.clientConfigService, configType);
            this.configs.set(configType, config);
        }

        return this.configs.get(configType);
    }
}
